import { VerificationTechniquesType } from './verification';

export type ImageViwerTableViewsTypes = 'MAXIMIZE-HIGH' | 'MAXIMIZE-LOW' | 'MINIMIZE' | 'SEPARATE' | 'PAGE-LOAD';

export interface ImageViewerGetDocumentBytesPayload {
  batchID: number | undefined;
  fileID: number;
  pageNo: number;
  imageFormat?: 'png';
}

export type ImageViewerGetDocumentBytesResponse = {
  pageBytes: string;
};

export interface ImageViewerGetFileLevelPayload {
  fileID: number;
}

export type ImageViewerGetFileLevelResponse = {
  fileID: number;
  batchID: number;
  fileName: string;
  batchPath: string;
  extractedCount: number;
  modifiedCount: number;
  accuracy: number;
  totalPages: number;
  docTypeID: number;
  processID: number;
};

export interface ImageViewerGetFilePDFPayload {
  fileId: number;
}

export interface SearchTextInFilePayload {
  fileID: number;
  searchText: string;
}

export interface SearchTextInFileResponse {
  index: number;
  lineNo: number;
  pageNo: number;
  xaxis: number;
  yaxis: number;
  width: number;
  height: number;
  confidenceScore: number;
  text: string;
  coordinate: string;
  roi: string;
}

export interface GetFileExceptionReasonList {
  fileExceptionID: number;
  fileException: string;
}

export interface AddFileCommentAndMoveToExceptionPayload {
  fileID: number;
  fileExceptionID: number;
  comments: string;
  module?: string;
}

export interface GetFileCommentsPayload {
  fileID: number;
}

export interface GetFileCommentsResponse {
  comment: string;
  createBy: string;
  createOn: string;
}

export interface GetFileHeaderDataPayload {
  batchID: number;
  fileId: number;
}

export interface FileHeaderChildData {
  fileStatus: number;
  childID: number;
  fieldID: number;
  fileID: number;
  resultID: string;
  extractedResult: string;
  updatedResult: string;
  confidenceScore: number;
  confidenceFlag: string;
  pageNumber: number;
  resultROI: string;
  isActive: boolean;
  patternValue: string;
  patternROI: string;
}

export interface FileHeaderData {
  batchID: number;
  fileID: number;
  batchPath: string;
  fieldID: number;
  fieldName: string; // Label
  extractedResult: string; // Value
  fieldValue: string;
  roi: string;
  extractedROI: string; // ROI
  pageNumber: number;
  resultID: number;
  isUpdated: boolean;
  extractedCount: number;
  updatedCount: number;
  confidenceFlag: string; // Color flag
  previousValue: string;
  previousConfidenceFlag: string;
  rowID: number;
  columnID: number;
  documentTypeID: number;
  isNestedMode: boolean;
  dataType: string;
  minimumLength: number;
  maximumLength: number;
  isBusinessRule: boolean;
  fieldModeID: number;
  isMandatory: boolean;
  patternValue: string;
  modeDescription: string;
  childCount: number;
  dataTypeRegex: string;
  fieldType: string;
  fieldTypeID: number;
  isValidationRequired: boolean;
  signTypeID: number;
  isDeleted: boolean;
  isMerged: boolean;
  confidenceScore: number;
  fieldOrder: number;
  isMultiValue: boolean;
  isAutoQC: boolean;
  isConfidenceFlag: boolean;
  isFieldModelTrained: boolean;
  childID?: number;
}

export interface TechniqueNameListTypes {
  fieldID: number;
  techniqueID: number;
  techniqueName: string;
  techniqueType: VerificationTechniquesType;
  patternLabel?: any;
}

export interface GetFileHeaderDataResponse {
  headerData: (FileHeaderData & {
    techniqueNameList?: TechniqueNameListTypes[];
    childData?: FileHeaderData[];
    childID?: number;
    isChild?: boolean;
  })[];
  filedetail: ImageViewerGetFileLevelResponse;
}

export interface RectMetricsForRubberBandType {
  left: number;
  top: number;
  width: number;
  height: number;
  pageNo?: number;
  showDialog?: boolean;
}

interface CanvasRef {
  highlightRubberBand: (_a?: RectMetricsForRubberBandType | null) => void;
}

export interface UpdateFieldHeaderDataPayload {
  isHeader: boolean;
  resultID: number;
  fileID: number;
  fieldID: number;
  currentValue: string;
  updateValue: string;
  updateValuePageNo: number;
  confidenceFlag: string;
  updateValueROI: string;
  originalROI: string;
  isSwap?: boolean;
  childID?: number;
  batchID: number;
}

export type CavseRefType = CanvasRef | null;

export type ImageViwerNavigateFile = (direction: 'NEXT' | 'PREV') => (isCompletedQc?: boolean | any) => void;

export enum FileStatus {
  QC_INCOMPLETE = 'QC Incomplete',
  QC_VERIFIED = 'QC Verified'
}
type FileStatusTypes = 'QC Incomplete' | 'QC Verified' | 'Ready for QC';

export interface UpdateFileStatusPayload {
  fileID: number;
  batchID: number;
  status: FileStatusTypes;
  accuracy: string;
}

export interface GetQaTablesListPayload {
  fileId: number;
  fieldId?: number;
}

export interface QaTablesListTypes {
  tableName: string;
  fieldID: number;
  columnNames: string[];
}

export interface GetQaTablesFieldsListPayload {
  fileId: number;
  fieldId: number;
}

export interface GetQaTablesFieldsListResponse {
  fileID: number;
  fieldID: number;
  fieldName: string;
  extractedResult: string;
  fieldValue: string;
  roi: string;
  extractedROI: string;
  pageNumber: number;
  resultID: number;
  isUpdated: boolean;
  extractedCount: number;
  updatedCount: number;
  updatedBy: null;
  confidenceFlag: string;
  previousValue: string;
  previousConfidenceFlag: '';
  rowID: number;
  columnID: number;
  dataType: string;
  minimumLength: number;
  maximumLength: number;
  isBusinessRule: true;
  patternValue: string;
  childCount: number;
  isMerged: boolean;
  isAutoQC: boolean;
  techID: number;
  originalColumnID: number;
  columnName?: string;
  rowStatus?: string;
}

export interface QaTableCellsDataType {
  [key: string]: any;
}

export interface QcTableRowData {
  tableDataMap: Map<string, GetQaTablesFieldsListResponse>;
  tableRows: QaTableCellsDataType[];
  original: GetQaTablesFieldsListResponse[];
}

export interface UpdateQcTableCellPayload {
  resultID: number;
  updateValue: string;
  ROI: string;
  fileID?: number;
  pageNumber?: number;
  fieldId?: number;
  columnID?: number;
  columnName?: string;
  techID?: number;
  originalColumnID?: number;
}

export interface UpdateQcTableRowPayload {
  rowID: number;
  fieldId: number;
  fileID: number;
  pageNumber?: number;
}

export interface UpdateQcTableRowPayload {
  rowID: number;
  fieldId: number;
  fileID: number;
  pageNumber?: number;
}

export interface AddQcTableRowPayload {
  rowID: number;
  fileId: number;
  fieldId: number;
}

export type OnClickRowActionsType = (rowID: number, action: 'CLEAR' | 'REVERT' | 'DELETE') => void;

export interface MergeQcTableRowsPayload {
  rowIDs: string;
  fileId: number;
  fieldId: number;
}

export interface SplitQcTablePayload {
  fileID: number;
  fieldID: number;
  rowId: number;
  columnID: number;
  pageNumber: number;
  originalCellROI: string;
  fieldValue: string;
  firstHalfText: string;
  isAllRows: boolean;
  type: string;
}

export interface DeleteQaTableRowMultiplePayload {
  rowIDs: string;
  fileId: number;
  fieldId: number;
}

const typePrefix = 'IMAGE_VIEWER_';

export const IMAGE_VIEWER_DUAL_SCREEN_ACTIONS = {
  LATEST_STATE: `${typePrefix}LATEST_STATE`,
  GET_LATEST_STATE: `${typePrefix}GET_LATEST_STATE`,
  CLIENT_STATE_UPDATED: `${typePrefix}CLIENT_STATE_UPDATED`,
  ON_SELECTE_FIELD: `${typePrefix}ON_SELECTE_FIELD`,
  ON_SELECTE_TABLE_CELL: `${typePrefix}ON_SELECTE_TABLE_CELL`,
  UPDATE_FIELD_ROI: `${typePrefix}UPDATE_FIELD_ROI`
};
